import React, { useState, useEffect, useCallback, memo, useRef } from "react";
import { StarsIcon } from "@/constants/icons";
import { useTranslation } from "next-i18next";

import { toggleModal } from "@/store/slices";
import { useAppDispatch } from "@/store/hook";
import { TPageDataHero } from "@/types/pageData";

import {
  Wrapper,
  BackgroundVideo,
  Content,
  MyButton,
  ContentWrapper,
  Layer,
} from "./style";
import toast from "react-hot-toast";

type Props = {
  data: TPageDataHero | undefined;
};

// Memoized Hero component for better performance
const Hero = memo(({ data }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isMobile, setIsMobile] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Detect mobile on client side only
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 976);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Delay video loading until after LCP (3 seconds after mount)
  useEffect(() => {
    if (!data?.background_video_url) {
      return;
    }

    const timer = setTimeout(() => {
      setShowVideo(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [data?.background_video_url]);

  const openForm = useCallback(() => {
    dispatch(
      toggleModal({
        key: isMobile ? "leadFormDrawer" : "leadForm",
        data: {
          data: {},
          open: true,
        },
      })
    );
  }, [dispatch, isMobile]);

  return (
    <Wrapper>
      <Layer />

      {/* Background IMAGE is the LCP element - renders immediately via CSS */}
      <div
        className="hero-bg-image"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: "url(/home/about/hero.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          zIndex: 1,
          opacity: videoLoaded ? 0 : 1,
          transition: "opacity 0.5s ease-in-out",
        }}
        aria-hidden="true"
      />

      {/* Video only loads 3 seconds AFTER page load - NOT part of LCP */}
      {showVideo && data?.background_video_url && (
        <BackgroundVideo
          src={data?.background_video_url}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster="/home/about/hero.webp"
          disablePictureInPicture
          controls={false}
          webkit-playsinline="true"
          x-webkit-airplay="deny"
          onLoadedData={() => setVideoLoaded(true)}
          onCanPlay={() => setVideoLoaded(true)}
        />
      )}

      <ContentWrapper>
        <Content>
          <h1
            suppressHydrationWarning
            dangerouslySetInnerHTML={{
              __html: t("SECTION.HERO.TITLE"),
            }}
          />

          <p>{t("SECTION.HERO.DESC")}</p>

          <MyButton
            onClick={openForm}
            type="button"
            aria-label="Open registration form"
          >
            <span className="icon">
              <StarsIcon />
            </span>
            <div className="text">
              {t(isMobile ? "SECTION.HERO.BUTTON" : "SECTION.HERO.BUTTON2")}
            </div>
          </MyButton>
        </Content>
      </ContentWrapper>
    </Wrapper>
  );
});

Hero.displayName = "Hero";

export default Hero;
